import { drop, first, groupBy, isNil, sortBy, sumBy } from 'lodash';
import { DateTime } from 'luxon';
import ITimeEntry, { IEntryRange } from 'types/TimeEntry';
import ITimeRange from 'types/TimeRange';
import TimeEntryBreakStatusType from 'types/enum/TimeEntryBreakStatusType';
import { default as IBreak } from '../types/TimeEntryBreak';
import { mapNotNull } from './collectionUtils';
import DateTimeFormat from './constants/dateTimeFormat';
import {
  dateTimeFromISOKeepZone,
  dateTimeFromISOWithoutZone,
  getDateTimesBetween,
  localizedDateTimeOrNow,
  nightShiftAdjustment,
  timeInRange,
} from './dateUtils';
import { typedObjectValues } from './objectUtils';
import {
  clampTimeEntryRangeToDate,
  getTimeEntryWorkedRanges,
  getTimeEntryWorkedRangesIso,
  getTotalTimeFromEntryRangeAndBreakRanges,
  timeEntryEndTimeOrNow,
} from './timeEntryUtils';

/**
 * Returns total in seconds for the duration of a time entry break represented as a time range.
 * If creating a time range from a time entry break and using `DateTime.fromIso()` make sure to pass the option
 * `{setZone: true})` so that the total can be calculated appropriately if the data is in a different timezone.
 *
 * @param breakRange the time range that represents the time entry break
 */
export function getTotalFromDateTimes(breakRange: ITimeRange<DateTime>) {
  const breakStartTime = breakRange.startTime.toUTC(0, { keepLocalTime: true });
  const breakEndTime = localizedDateTimeOrNow(breakRange.endTime);
  let totalBreakTime = Math.abs(breakEndTime.toMillis() - breakStartTime.toMillis());
  if (totalBreakTime < 0) {
    totalBreakTime = 0;
  }
  return totalBreakTime / 1000;
}

export function isOutsideRange(entryRange: ITimeRange<DateTime>, breakRanges: Array<ITimeRange<DateTime>>) {
  let isError = false;
  for (const value of breakRanges) {
    const nightShiftedAdjustment = nightShiftAdjustment(value);
    if (
      !timeInRange(
        nightShiftedAdjustment.startTime,
        { startTime: entryRange.startTime, endTime: entryRange.endTime },
        true
      ) &&
      !timeInRange(
        nightShiftedAdjustment.endTime,
        { startTime: entryRange.startTime, endTime: entryRange.endTime },
        true
      )
    ) {
      isError = true;
    }
  }
  return isError;
}

export function getBreakStatus(
  entryBreak: Pick<IBreak, 'startTime' | 'endTime'>,
  entry: Pick<ITimeEntry, 'startTime' | 'endTime'>
) {
  const entryStartTime = dateTimeFromISOWithoutZone(entry.startTime).startOf('minute');
  const entryEndTime = timeEntryEndTimeOrNow(entry).startOf('minute');
  const breakStartTime = dateTimeFromISOWithoutZone(entryBreak.startTime).startOf('minute');
  const breakEndTime = breakEndTimeOrNow(entryBreak, entry.startTime).startOf('minute');

  if (breakStartTime >= entryStartTime && breakEndTime <= entryEndTime) {
    // Break is entirely inside entry.
    return TimeEntryBreakStatusType.INSIDE_ENTRY;
  } else if (breakStartTime <= entryStartTime && breakEndTime >= entryEndTime) {
    // Break startTime and endTime is outside of entry.
    return TimeEntryBreakStatusType.CONTAINS_ENTRY;
  } else if (breakStartTime >= entryStartTime && breakEndTime >= entryEndTime && breakStartTime <= entryEndTime) {
    // Breaks endTime is outside of entry.
    return TimeEntryBreakStatusType.END_TIME_OUTSIDE;
  } else if (breakStartTime <= entryStartTime && breakEndTime <= entryEndTime && breakEndTime >= entryStartTime) {
    // Breaks startTime is outside of entry.
    return TimeEntryBreakStatusType.START_TIME_OUTSIDE;
  }
  return TimeEntryBreakStatusType.OUTSIDE_ENTRY; // Default to outside to easily catch errors.
}

/**
 * Calculates the duration of a given break within a time entry.
 *
 * @param entryBreak break entry to check duration
 * @param entry the time entry that the break belongs to
 * @returns the number of seconds that the break spans
 */
export function getBreakTotal(entryBreak: IEntryRange, entry: IEntryRange) {
  const entryStartTime = dateTimeFromISOWithoutZone(entry.startTime).startOf('minute');
  const entryEndTime = timeEntryEndTimeOrNow(entry).startOf('minute');
  const breakStartTime = dateTimeFromISOWithoutZone(entryBreak.startTime).startOf('minute');
  const breakEndTime = breakEndTimeOrNow(entryBreak, entry.startTime).startOf('minute');

  const startTime = entryStartTime < breakStartTime ? breakStartTime : entryStartTime;
  const endTime = entryEndTime > breakEndTime ? breakEndTime : entryEndTime;
  const totalBreakTime = endTime.toMillis() - startTime.toMillis();
  if (totalBreakTime <= 0) {
    return 0;
  } else {
    return totalBreakTime / 1000;
  }
}

export function breakEndTimeOrNow(brk: IEntryRange, entryStart: string): DateTime {
  if (!isNil(brk.endTime)) {
    return dateTimeFromISOWithoutZone(brk.endTime);
  } else {
    const entryStartDateTime = dateTimeFromISOKeepZone(entryStart);
    return DateTime.local()
      .setZone(entryStartDateTime.zoneName, { keepLocalTime: false })
      .toUTC(0, { keepLocalTime: true })
      .startOf('minute');
  }
}

export function breakIsUnderRequiredMinutes(breakStartTime: string, zone: string, breakDurationSeconds: number) {
  return (
    DateTime.now().toSeconds() -
      dateTimeFromISOWithoutZone(breakStartTime).setZone(zone, { keepLocalTime: false }).toSeconds() <
    breakDurationSeconds
  );
}

export function getAvailableBreakEndTime(breakStartDeviceTime: string, zone: string, breakDuration: number) {
  return dateTimeFromISOKeepZone(breakStartDeviceTime)
    .setZone(zone, { keepLocalTime: false })
    .plus({ seconds: breakDuration })
    .toFormat(DateTimeFormat.timeSimple);
}

type AutomaticBreakSetting = 'interval' | 'fixed';

export type AutomaticBreakSettingPayload = {
  type: AutomaticBreakSetting;
  offsetSeconds: number[];
  breakSeconds: number;
};

export type AutomaticBreakResult = {
  targetTimeEntryId: string;
  timeRange: ITimeRange<DateTime>;
};

export type TimeEntryRange = {
  id: string;
  startTime: string;
  endTime: string;
  breaksInRange: IEntryRange[];
};

export type TGetAutomaticalIntervalEligibleBreakOffsetsArgs = {
  totalTimeSeconds: number;
  totalExistingBreakSeconds: number;
  offsetSeconds: number[];
  breakSeconds: number;
};

export function getAutomaticIntervalEligibleBreakOffsets({
  totalTimeSeconds,
  totalExistingBreakSeconds,
  offsetSeconds,
  breakSeconds,
}: TGetAutomaticalIntervalEligibleBreakOffsetsArgs) {
  const amountOfBreaksAlreadyAdded = totalExistingBreakSeconds / breakSeconds;
  const fullBreaksAlreadyAdded = Math.floor(amountOfBreaksAlreadyAdded);

  const offsetsWithCurrentBreaks = drop(offsetSeconds, fullBreaksAlreadyAdded);

  const totalTimeWithoutCurrentBreaks = totalTimeSeconds - totalExistingBreakSeconds;
  const partialBreakSeconds = (amountOfBreaksAlreadyAdded - fullBreaksAlreadyAdded) * breakSeconds;

  const adjustedOffsets = offsetsWithCurrentBreaks.map((offset, index) => {
    const isFirstEligibleOffset = index === 0;

    if (isFirstEligibleOffset) {
      return offset;
    }

    const firstAdjustment = breakSeconds - partialBreakSeconds;
    const nonFirstAdjustment = (index - 1) * breakSeconds;

    return offset + firstAdjustment + nonFirstAdjustment;
  });

  return adjustedOffsets.filter((offset) => {
    return offset <= totalTimeWithoutCurrentBreaks;
  });
}

export type TAutomaticBreakPayload = {
  timeEntryRanges: TimeEntryRange[];
  offsetSeconds: number[];
  breakSeconds: number;
};

export function applyAutomaticIntervalBreaks({ timeEntryRanges, offsetSeconds, breakSeconds }: TAutomaticBreakPayload) {
  const segmentedByDayEntries = timeEntryRanges.flatMap((timeEntryRange) => {
    const startDateTime = DateTime.fromISO(timeEntryRange.startTime, { setZone: true }).startOf('day');
    const endDateTime = DateTime.fromISO(timeEntryRange.endTime, { setZone: true });

    const datesBetween = getDateTimesBetween(startDateTime, endDateTime);
    return datesBetween.map((dateBetween) => clampTimeEntryRangeToDate(timeEntryRange, dateBetween));
  });

  const groupedByDayStart = groupBy(segmentedByDayEntries, (entryRange) =>
    DateTime.fromISO(entryRange.startTime, { setZone: true }).startOf('day').toSeconds()
  );

  return typedObjectValues(groupedByDayStart).flatMap((clampedTimeEntryRanges) => {
    const totalTimeSeconds = sumBy(clampedTimeEntryRanges, (timeEntryRange) =>
      getTotalTimeFromEntryRangeAndBreakRanges(timeEntryRange, timeEntryRange.breaksInRange)
    );

    const totalExistingBreakSeconds = sumBy(clampedTimeEntryRanges, (timeEntryRange) => {
      return sumBy(timeEntryRange.breaksInRange, (breakInRange) => getBreakTotal(timeEntryRange, breakInRange));
    });

    const amountOfBreaksAlreadyAdded = totalExistingBreakSeconds / breakSeconds;
    const fullBreaksAlreadyAdded = Math.floor(amountOfBreaksAlreadyAdded);

    const partialBreakSeconds = (amountOfBreaksAlreadyAdded - fullBreaksAlreadyAdded) * breakSeconds;

    const adjustedOffsetsAlreadyReached = getAutomaticIntervalEligibleBreakOffsets({
      totalExistingBreakSeconds,
      totalTimeSeconds,
      offsetSeconds,
      breakSeconds,
    });

    const breaksToAdd = adjustedOffsetsAlreadyReached.flatMap((offsetSeconds, index) => {
      const isFirstEligibleOffset = index === 0;

      const offsetsToAdd = getBreaksAtOffset({
        timeEntryRanges: clampedTimeEntryRanges,
        offsetSeconds,
        breakDurationSeconds: isFirstEligibleOffset ? breakSeconds - partialBreakSeconds : breakSeconds,
      });

      return offsetsToAdd;
    });

    return breaksToAdd;
  });
}

export type TGetBreaksAtOffsetPayload = {
  timeEntryRanges: TimeEntryRange[];
  offsetSeconds: number;
  breakDurationSeconds: number;
};

export function getBreaksAtOffset({ timeEntryRanges, offsetSeconds, breakDurationSeconds }: TGetBreaksAtOffsetPayload) {
  const timeEntrySegments = timeEntryRanges.flatMap((timeEntryRange) => {
    const segments = getTimeEntryWorkedRanges(timeEntryRange);

    return segments.map((segment) => ({
      id: timeEntryRange.id,
      startTime: segment.startTime,
      endTime: segment.endTime,
    }));
  });

  const sortedSegments = sortBy(timeEntrySegments, (timeEntrySegment) => timeEntrySegment.startTime);
  const firstSegmentStart = first(sortedSegments)?.startTime;

  if (!firstSegmentStart) {
    return [];
  }

  const { breakSegments, breakSecondsRemaining } = sortedSegments.reduce(
    (acc, segment) => {
      if (acc.breakSecondsRemaining === 0) {
        return acc;
      }

      const isAddingBreak = breakDurationSeconds !== acc.breakSecondsRemaining;

      if (isAddingBreak) {
        const targetEnd = segment.startTime + acc.breakSecondsRemaining;
        const doesBreakFit = targetEnd < segment.endTime;

        const breakSegment = {
          timeEntryId: segment.id,
          startTime: segment.startTime,
          endTime: doesBreakFit ? targetEnd : segment.endTime,
        };

        return {
          breakSecondsRemaining: acc.breakSecondsRemaining - (breakSegment.endTime - breakSegment.startTime),
          breakSegments: acc.breakSegments.concat(breakSegment),
          offsetSeconds: acc.offsetSeconds - (segment.endTime - segment.startTime),
        };
      }

      const isOffsetInSegment = segment.startTime + acc.offsetSeconds < segment.endTime;

      if (!isOffsetInSegment) {
        return {
          ...acc,
          offsetSeconds: acc.offsetSeconds - (segment.endTime - segment.startTime),
        };
      }

      const targetEnd = segment.startTime + acc.offsetSeconds + acc.breakSecondsRemaining;
      const doesBreakFit = targetEnd < segment.endTime;

      const breakSegment = {
        timeEntryId: segment.id,
        startTime: segment.startTime + acc.offsetSeconds,
        endTime: doesBreakFit ? targetEnd : segment.endTime,
      };

      return {
        breakSecondsRemaining: acc.breakSecondsRemaining - (breakSegment.endTime - breakSegment.startTime),
        breakSegments: acc.breakSegments.concat(breakSegment),
        offsetSeconds: acc.offsetSeconds - (segment.endTime - segment.startTime),
      };
    },
    {
      breakSecondsRemaining: breakDurationSeconds,
      breakSegments: new Array<ITimeRange<number> & { timeEntryId: string }>(),
      offsetSeconds,
    }
  );

  if (breakSecondsRemaining !== 0) {
    return [];
  }

  return breakSegments;
}

export function applyAutomaticFixedBreaks({ timeEntryRanges, breakSeconds, offsetSeconds }: TAutomaticBreakPayload) {
  const segmentedByDayEntries = timeEntryRanges.flatMap((timeEntryRange) => {
    const startDateTime = DateTime.fromISO(timeEntryRange.startTime, { setZone: true }).startOf('day');
    const endDateTime = DateTime.fromISO(timeEntryRange.endTime, { setZone: true });

    const datesBetween = getDateTimesBetween(startDateTime, endDateTime);
    return datesBetween.map((dateBetween) => clampTimeEntryRangeToDate(timeEntryRange, dateBetween));
  });

  const groupedByDayStart = groupBy(segmentedByDayEntries, (entryRange) =>
    DateTime.fromISO(entryRange.startTime, { setZone: true }).startOf('day').toSeconds()
  );

  return Object.values(groupedByDayStart).flatMap((clampedTimeEntryRanges) => {
    const timeEntrySegments = clampedTimeEntryRanges.flatMap((timeEntryRange) => {
      const segments = getTimeEntryWorkedRangesIso(timeEntryRange);

      return segments.map((segment) => ({
        id: timeEntryRange.id,
        startTime: segment.startTime,
        endTime: segment.endTime,
      }));
    });

    const sortedSegments = sortBy(timeEntrySegments, (timeEntrySegment) => timeEntrySegment.startTime);
    const targetOffsetRanges = offsetSeconds.map((offset) => ({ startTime: offset, endTime: offset + breakSeconds }));

    return sortedSegments.flatMap((segment) => {
      const segmentStart = DateTime.fromISO(segment.startTime, { setZone: true });
      const segmentEnd = DateTime.fromISO(segment.endTime, { setZone: true });

      const segmentDayStart = segmentStart.startOf('day');

      const timeEntryOffsetStartSeconds = segmentStart.toSeconds() - segmentDayStart.toSeconds();
      const timeEntryOffsetEndSeconds = segmentEnd.toSeconds() - segmentDayStart.toSeconds();

      return mapNotNull(targetOffsetRanges, (targetOffsetRange) => {
        const isInRange =
          timeEntryOffsetStartSeconds <= targetOffsetRange.endTime &&
          timeEntryOffsetEndSeconds >= targetOffsetRange.startTime;

        if (!isInRange) {
          return null;
        }

        const startTime =
          timeEntryOffsetStartSeconds > targetOffsetRange.startTime
            ? timeEntryOffsetStartSeconds
            : targetOffsetRange.startTime;

        const endTime =
          timeEntryOffsetEndSeconds < targetOffsetRange.endTime ? timeEntryOffsetEndSeconds : targetOffsetRange.endTime;

        if (startTime === endTime) {
          return null;
        }

        return {
          timeEntryId: segment.id,
          startTime: startTime + segmentDayStart.toSeconds(),
          endTime: endTime + segmentDayStart.toSeconds(),
        };
      });
    });
  });
}

export function displayStartTime(
  entryBreak: Pick<IBreak, 'startTime'>,
  entry: Pick<ITimeEntry, 'startTime'>
): DateTime {
  const breakStart = dateTimeFromISOKeepZone(entryBreak.startTime);
  const entryStartOffset = dateTimeFromISOKeepZone(entry.startTime).offset;
  const breakStartOffset = breakStart.offset;
  return dateTimeFromISOWithoutZone(entryBreak.startTime)
    .plus({ minutes: breakStartOffset - entryStartOffset })
    .startOf('minute')
    .setZone(breakStart.zoneName, { keepLocalTime: true });
}

export function displayEndTime(
  entryBreak: Pick<IBreak, 'startTime' | 'endTime'>,
  entry: Pick<ITimeEntry, 'startTime'>
): DateTime {
  const entryStartOffset = dateTimeFromISOKeepZone(entry.startTime).offset;

  if (isNil(entryBreak.endTime)) {
    const breakStart = dateTimeFromISOKeepZone(entryBreak.startTime);
    const breakStartOffset = breakStart.offset;
    return DateTime.local()
      .plus({ minutes: breakStartOffset - entryStartOffset })
      .startOf('minute')
      .setZone(breakStart.zoneName, { keepLocalTime: true });
  }

  const breakEnd = dateTimeFromISOKeepZone(entryBreak.endTime);
  const breakEndOffset = breakEnd.offset;
  return dateTimeFromISOWithoutZone(entryBreak.endTime)
    .plus({ minutes: breakEndOffset - entryStartOffset })
    .startOf('minute')
    .setZone(breakEnd.zoneName, { keepLocalTime: true });
}
